'use client'

import MedicalCertificateQuestionnaireContainer from '@/shared/containers/MedicalCertificateQuestionnaireContainer'
import { Formik, FormikHelpers } from 'formik'
import React, { useState } from 'react'
import { MedicalCertificate } from '@/lib/questionnaires/MedicalCertificate'
import { useStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { axiosErrorToString } from '@/lib/utils'
import { useMutation } from '@tanstack/react-query'
import { RequestMedicalCertificate } from '@/lib/apis/requests'
import { ErrorType, PatientProfile, ProfileStatus } from '@/lib/apis/types'
import { isHttpError } from '@/lib/apis/error'
import Modal, { ModalVariant } from '@/shared/components/Modal'
import useApiClient from '@/lib/apis/clients/useApiClient'
import SickLeaveQuestionnaire = MedicalCertificate.SickLeaveQuestionnaire
import CarerLeaveQuestionnaire = MedicalCertificate.CarerLeaveQuestionnaire
import CarerLeaveQuestionnaireValues = MedicalCertificate.CarerLeaveQuestionnaireValues
import SickLeaveQuestionnaireValues = MedicalCertificate.SickLeaveQuestionnaireValues
import SickLeaveMultiDayQuestionnaire = MedicalCertificate.SickLeaveMultiDayQuestionnaire
import CarerLeaveMultiDayQuestionnaire = MedicalCertificate.CarerLeaveMultiDayQuestionnaire

type LoggedInUser = {
  completed: boolean
  profile: PatientProfile
}

type Props = {
  type: 'SICK' | 'CARER'
  profileStatus: ProfileStatus | null
  user?: LoggedInUser
  multipleDays: boolean
}

export default function MedicalCertificateRequestForm({
  type,
  profileStatus,
  user,
  multipleDays,
}: Props) {
  const api = useApiClient()
  const mutation = useMutation(
    RequestMedicalCertificate.mutation(api, {
      authenticated: user !== undefined,
    }),
  )

  const showNotification = useStore((state) => state.showNotification)
  const [error, setError] = useState<ErrorType>()

  function submitRequest(values: any, { setSubmitting }: FormikHelpers<any>) {
    mutation
      .mutateAsync({ values })
      .then((result) => window.location.assign(result.checkoutUrl))
      .catch((err) => {
        setSubmitting(false)

        if (isHttpError(err) && err.response?.error === 'MISMATCH_EMAIL') {
          setError('MISMATCH_EMAIL')
        } else {
          showNotification(
            new NotificationItem(
              axiosErrorToString(err),
              NotificationItemVariant.Error,
            ),
          )
        }
      })
  }

  let child = null
  switch (type) {
    case 'SICK':
      child = (
        <SickLeaveForm
          user={user}
          profileStatus={profileStatus}
          submitForm={submitRequest}
          multipleDays={multipleDays}
        />
      )
      break
    case 'CARER':
      child = (
        <CarerLeaveForm
          user={user}
          profileStatus={profileStatus}
          submitForm={submitRequest}
          multipleDays={multipleDays}
        />
      )
      break
  }

  let modal = null
  switch (error) {
    case 'MISMATCH_EMAIL':
      modal = (
        <Modal
          variant={ModalVariant.Error}
          title="Invalid email"
          body={`The email submitted is different from the email you signed up with. If you want to change your email, you can go to your profile and update.`}
          onClose={() => setError(undefined)}
        />
      )
  }

  return (
    <div className="mx-auto">
      {child}
      {modal}
    </div>
  )
}

type SickLeaveRequestFormProps = {
  profileStatus: ProfileStatus | null
  user?: LoggedInUser
  submitForm: (
    values: SickLeaveQuestionnaireValues,
    helpers: FormikHelpers<any>,
  ) => void
  multipleDays: boolean
}

type CarerLeaveRequestFormProps = {
  profileStatus: ProfileStatus | null
  user?: LoggedInUser
  submitForm: (
    values: CarerLeaveQuestionnaireValues,
    helpers: FormikHelpers<any>,
  ) => void
  multipleDays: boolean
}

function SickLeaveForm({
  profileStatus,
  user,
  submitForm,
  multipleDays,
}: SickLeaveRequestFormProps) {
  const schema = multipleDays
    ? MedicalCertificate.SickLeaveMultiDayQuestionnaireSchema
    : MedicalCertificate.SickLeaveQuestionnaireSchema

  const defaultValues = schema.cast({
    ...(user?.profile ?? {}),
    mcType: 'Sick',
  })

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={submitForm}
      validationSchema={schema}
      validateOnMount={true}
    >
      {(props) => {
        const questionnaires = multipleDays
          ? SickLeaveMultiDayQuestionnaire(props.values)
          : SickLeaveQuestionnaire(props.values)

        return (
          <MedicalCertificateQuestionnaireContainer
            authenticated={Boolean(user)}
            profileStatus={profileStatus}
            initialProfile={user?.profile ?? null}
            questionnaire={
              user && user.completed && profileStatus?.mobileVerified === true
                ? {
                    pages: questionnaires.pages.filter(
                      (page) => page !== 'personal-information',
                    ),
                  }
                : questionnaires
            }
            certificateType="SICK"
            multipleDays={multipleDays}
          />
        )
      }}
    </Formik>
  )
}

function CarerLeaveForm({
  profileStatus,
  user,
  submitForm,
  multipleDays,
}: CarerLeaveRequestFormProps) {
  const schema = multipleDays
    ? MedicalCertificate.CarerLeaveMultiDayQuestionnaireSchema
    : MedicalCertificate.CarerLeaveQuestionnaireSchema

  let defaultValues = schema.cast({
    ...(user?.profile ?? {}),
    mcType: 'Carer',
  })

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={submitForm}
      validationSchema={schema}
      validateOnMount={true}
    >
      {(props) => {
        const questionnaires = multipleDays
          ? CarerLeaveMultiDayQuestionnaire(props.values)
          : CarerLeaveQuestionnaire(props.values)

        return (
          <MedicalCertificateQuestionnaireContainer
            authenticated={Boolean(user)}
            profileStatus={profileStatus}
            initialProfile={user?.profile ?? null}
            questionnaire={
              user && user.completed
                ? {
                    pages: questionnaires.pages.filter(
                      (page) => page !== 'personal-information',
                    ),
                  }
                : questionnaires
            }
            certificateType="CARER"
            multipleDays={multipleDays}
          />
        )
      }}
    </Formik>
  )
}
